<script setup>
import { ref, getCurrentInstance } from 'vue';
import { useForm, router } from '@inertiajs/vue3';
import { useToast } from 'primevue/usetoast';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';

import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Textarea from 'primevue/textarea';
import Select from 'primevue/select';
import Checkbox from 'primevue/checkbox';
import Card from 'primevue/card';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import Dialog from 'primevue/dialog';
import RadioButton from 'primevue/radiobutton';
import Message from 'primevue/message';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();

const props = defineProps({
    site: Object,
    coins: Object,
    categories: Array,
    languages: Array,
    countries: Array,
    sellers: Array,
    teams: Array,
    types: Array,
    note: Object,
});

// Tab management
const activeIndex = ref(0);

// Dialog for confirmation
const confirmDialog = ref(false);

// Define sellerId for admin panel - in admin context, we should be able to edit all pricing tables
const sellerId = window.sellerId || null; // null means admin can edit all

// Helper function to check if current user can edit pricing for this seller
const canEditPricing = (sellerIdToCheck) => {
    // In admin panel, we can edit all pricing tables
    return sellerId === null || sellerId === sellerIdToCheck;
};

// Form for site data
const form = useForm({
    id: props.site.id,
    url: props.site.url,
    category_id: props.site.category_id,
    da: props.site.da,
    dr: props.site.dr,
    traffic: props.site.traffic,
    language_id: props.site.language_id,
    country_id: props.site.country_id,
    link_type: props.site.link_type,

    gambling: props.site.gambling ?? false,
    cdb: props.site.cdb ?? false,
    cripto: props.site.cripto ?? false,
    sponsor: props.site.sponsor ?? false,
    menu: props.site.menu ?? false,
    banner: props.site.banner ?? false,

    last_posted: props.site.last_posted,
    seller_id: props.site.seller_id,
    team_id: props.site.team_id,

    obs: props.site.obs,

    owner_name: props.site.owner_name,
    owner_email: props.site.owner_email,
    owner_phone: props.site.owner_phone,
    bank: props.site.bank,
    pix: props.site.pix,
    phone: props.site.phone,
    paypal: props.site.paypal,
    instagram: props.site.instagram,
    facebook: props.site.facebook,

    owner_role: props.site.owner_role,
    promo: props.site.promo ?? false,
    restrict: props.site.restrict ?? false,
    restrict_detail: props.site.restrict_detail,
    archive_article: props.site.archive_article ?? false,
    archive_due: props.site.archive_due,
    links: props.site.links,
    embed: props.site.embed ?? false,
    images: props.site.images,
    example_article: props.site.example_article,
    rules: props.site.rules,
    google_news: props.site.google_news ?? false,
    global_account: props.site.global_account,

    offers: props.site.offers.map(o => {
        const offer = JSON.parse(JSON.stringify(o));
        return {
            ...offer,
            default: {
                id: offer.default.id,
                available: true,
                cost: offer.default.pivot.cost,
                cost_coin: offer.default.pivot.cost_coin,
                sale: offer.default.pivot.sale,
                sale_coin: offer.default.pivot.sale_coin,
            },
            types: props.types.map(_t => {
                const type = JSON.parse(JSON.stringify(_t));
                const t = offer.types.find(t => t.id == type.id);
                
                if(null == t) return type;
                
                type.available = true;
                type.cost = t.pivot.cost;
                type.cost_coin = t.pivot.cost_coin;
                type.sale = t.pivot.sale;
                type.sale_coin = t.pivot.sale_coin;
                
                return type;
            })
        };
    }),
});

// Form for note
const noteForm = useForm({
    text: props.note?.text ?? '',
});

// Options
const linkTypeOptions = [
    { label: 'DOFOLLOW', value: 'DOFOLLOW' },
    { label: 'NOFOLLOW', value: 'NOFOLLOW' },
];

const linksOptions = [
    { label: '3', value: 3 },
    { label: '5', value: 5 },
    { label: $t('No limit'), value: null },
];

const imagesOptions = [
    { label: '1', value: 1 },
    { label: '3', value: 3 },
    { label: '5', value: 5 },
];

// Methods
const computeSuggestedForType = (type) => {
    if (!type.cost || !type.cost_coin) return;
    
    let cost = parseFloat(type.cost.toString().replace(/[^0-9.]/g, '') || 0);
    
    const exchangeInfo = props.exchangeData?.[type.cost_coin?.toUpperCase()];
    
    if (!exchangeInfo) {
        type.suggested = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: type.cost_coin
        }).format(cost);
        return;
    }
    
    const fixedAddition = (exchangeInfo.fixed_addition || 0) / 100;
    const percentageAddition = (exchangeInfo.percentage_addition || 0) / 10000;
    const applyPercentageToTotal = exchangeInfo.apply_percentage_to_total || false;
    
    let suggestedPrice = 0;
    
    if (applyPercentageToTotal) {
        suggestedPrice = (cost + fixedAddition) * (1 + percentageAddition);
    } else {
        suggestedPrice = cost + (cost * percentageAddition) + fixedAddition;
    }
    
    type.suggested = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: type.cost_coin
    }).format(suggestedPrice);
};

const updateCoinForType = (type) => {
    type.sale_coin = type.cost_coin;
};

const submit = () => {
    form.transform(data => {
        const tdata = { ...data };
        
        // Handle offers transformation only if they exist and we're updating pricing
        if (tdata.offers && tdata.offers.length > 0 && activeIndex.value === 1) { // Offers tab index
            const linkarme = tdata.offers.find(offer => canEditPricing(offer.seller.id));
            
            delete tdata.offers;
            
            if (linkarme) {
                tdata.types = linkarme.types;
                tdata.types.push(linkarme.default);
            }
        } else if (tdata.offers) {
            // If not on pricing tab, don't send offers data
            delete tdata.offers;
        }
        
        return tdata;
    }).put(route('sites.update', props.site.id), {
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Site updated successfully',
                life: 3000
            });
        },
        onError: (error) => {
            console.log(error);
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to update site',
                life: 5000
            });
        }
    });
};

const submitNote = () => {
    noteForm.put(route('notes.update', props.site.id), {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Note updated successfully',
                life: 3000
            });
        }
    });
};

const approve = () => {
    form.put(route('sites.approve', props.site.id), {
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Site approved successfully',
                life: 3000
            });
        }
    });
};

const reject = () => {
    form.put(route('sites.reject', props.site.id), {
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Site rejected successfully',
                life: 3000
            });
        }
    });
};

const toggle = () => {
    form.post(route('sites.toggle', props.site.id), {
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: props.site.deleted_at ? 'Site reactivated successfully' : 'Site deactivated successfully',
                life: 3000
            });
        }
    });
};

const destroy = () => {
    form.delete(route('sites.destroy', props.site.id), {
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Site deleted successfully',
                life: 3000
            });
        }
    });
};

const addPricingTableForLinkarme = () => {
    router.post(route('sites.addPricingTableForLinkarme', props.site.id), {
        onSuccess: () => {
            window.location.reload();
        }
    });
};

const removePricingTable = (id) => {
    router.delete(route('sites.removePricingTable', { site: props.site.id, offer: id }), {
        onSuccess: () => {
            window.location.reload();
        }
    });
};

const getStatusSeverity = (status) => {
    switch (status) {
        case 'PENDING': return 'warn';
        case 'ENABLED': return 'success';
        case 'DISABLED': return 'danger';
        default: return 'info';
    }
};
</script>

<template>
    <AdminMainLayout>
        <!-- Site Header -->
        <div class="grid gap-6 mb-6">
            <div class="col-12">
                <Card>
                    <template #title>
                        <div class="flex align-items-center justify-content-between">
                            <div class="flex align-items-center gap-2">
                                <i class="pi pi-pencil text-2xl"></i>
                                <span>{{ $t('Edit Site') }}: {{ site.url }}</span>
                            </div>
                            <Tag :value="site.formatted_status" :severity="getStatusSeverity(site.status)" />
                        </div>
                    </template>
                    
                    <template #content>
                        <div class="flex gap-2">
                            <Button v-if="site.status === 'PENDING'" 
                                    :label="$t('Approve')" 
                                    icon="pi pi-check" 
                                    severity="success"
                                    @click="approve" 
                                    :loading="form.processing" />
                            
                            <Button v-if="site.status === 'PENDING'" 
                                    :label="$t('Reject')" 
                                    icon="pi pi-times" 
                                    severity="danger"
                                    @click="reject" 
                                    :loading="form.processing" />
                            
                            <Button v-if="site.deleted_at" 
                                    :label="$t('Reactivate')" 
                                    icon="pi pi-refresh" 
                                    severity="success"
                                    @click="toggle" 
                                    :loading="form.processing" />
                            
                            <Button v-else 
                                    :label="$t('Deactivate')" 
                                    icon="pi pi-ban" 
                                    severity="warn"
                                    @click="toggle" 
                                    :loading="form.processing" />
                            
                            <Button v-if="site.deleted_at" 
                                    :label="$t('Delete')" 
                                    icon="pi pi-trash" 
                                    severity="danger"
                                    @click="confirmDialog = true" />
                        </div>
                    </template>
                </Card>
            </div>
        </div>

        <!-- Tab View -->
        <TabView v-model:activeIndex="activeIndex">
            <!-- Site Tab -->
            <TabPanel>
                <template #header>
                    <i class="pi pi-globe mr-2"></i>
                    {{ $t('Site') }}
                </template>

                <div class="grid gap-6">
                    <!-- Note Section -->
                    <div class="col-12">
                        <Card>
                            <template #title>
                                <div class="flex align-items-center gap-2">
                                    <i class="pi pi-file-edit"></i>
                                    <span>{{ $t('Edit note') }}</span>
                                </div>
                            </template>
                            <template #content>
                                <form @submit.prevent="submitNote">
                                    <div class="field">
                                        <Textarea 
                                            id="note" 
                                            v-model="noteForm.text" 
                                            rows="3" 
                                            class="w-full"
                                            :placeholder="$t('Add a note about this site')" />
                                    </div>
                                    <Button type="submit" 
                                            :label="$t('Save Note')" 
                                            icon="pi pi-save"
                                            :loading="noteForm.processing" />
                                </form>
                            </template>
                        </Card>
                    </div>

                    <!-- Site Form -->
                    <div class="col-12">
                        <Card>
                            <template #title>
                                <div class="flex align-items-center gap-2">
                                    <i class="pi pi-cog"></i>
                                    <span>{{ $t('Site Information') }}</span>
                                </div>
                            </template>
                            <template #content>
                                <form @submit.prevent="submit">
                                    <div class="formgrid grid">
                                        <!-- URL -->
                                        <div class="field col-12">
                                            <label for="url" class="font-semibold">{{ $t('URL') }} <span class="text-red-500">*</span></label>
                                            <InputText 
                                                id="url" 
                                                v-model="form.url" 
                                                class="w-full"
                                                :class="{ 'p-invalid': form.errors.url }"
                                                :disabled="form.processing" />
                                            <small v-if="form.errors.url" class="p-error">{{ form.errors.url }}</small>
                                        </div>

                                        <!-- Category -->
                                        <div class="field col-12">
                                            <label for="category" class="font-semibold">{{ $t('Category') }}</label>
                                            <Select 
                                                id="category"
                                                v-model="form.category_id" 
                                                :options="categories" 
                                                optionLabel="name" 
                                                optionValue="id"
                                                :placeholder="$t('Select Category')"
                                                class="w-full"
                                                :class="{ 'p-invalid': form.errors.category_id }"
                                                :disabled="form.processing" />
                                            <small v-if="form.errors.category_id" class="p-error">{{ form.errors.category_id }}</small>
                                        </div>

                                        <!-- Metrics -->
                                        <div class="field col-12 md:col-4">
                                            <label for="da" class="font-semibold">{{ $t('DA') }}</label>
                                            <InputNumber 
                                                id="da" 
                                                v-model="form.da" 
                                                class="w-full"
                                                :class="{ 'p-invalid': form.errors.da }"
                                                :disabled="form.processing"
                                                :minFractionDigits="0"
                                                :maxFractionDigits="2" />
                                            <small v-if="form.errors.da" class="p-error">{{ form.errors.da }}</small>
                                        </div>

                                        <div class="field col-12 md:col-4">
                                            <label for="dr" class="font-semibold">{{ $t('DR') }}</label>
                                            <InputNumber 
                                                id="dr" 
                                                v-model="form.dr" 
                                                class="w-full"
                                                :class="{ 'p-invalid': form.errors.dr }"
                                                :disabled="form.processing"
                                                :minFractionDigits="0"
                                                :maxFractionDigits="2" />
                                            <small v-if="form.errors.dr" class="p-error">{{ form.errors.dr }}</small>
                                        </div>

                                        <div class="field col-12 md:col-4">
                                            <label for="traffic" class="font-semibold">{{ $t('Traffic') }}</label>
                                            <InputNumber 
                                                id="traffic" 
                                                v-model="form.traffic" 
                                                class="w-full"
                                                :class="{ 'p-invalid': form.errors.traffic }"
                                                :disabled="form.processing"
                                                :minFractionDigits="0"
                                                :maxFractionDigits="2" />
                                            <small v-if="form.errors.traffic" class="p-error">{{ form.errors.traffic }}</small>
                                        </div>

                                        <!-- Language and Country -->
                                        <div class="field col-12 md:col-6">
                                            <label for="language" class="font-semibold">{{ $t('Language') }}</label>
                                            <Select 
                                                id="language"
                                                v-model="form.language_id" 
                                                :options="languages" 
                                                optionLabel="name" 
                                                optionValue="id"
                                                :placeholder="$t('Select Language')"
                                                class="w-full"
                                                :class="{ 'p-invalid': form.errors.language_id }"
                                                :disabled="form.processing" />
                                            <small v-if="form.errors.language_id" class="p-error">{{ form.errors.language_id }}</small>
                                        </div>

                                        <div class="field col-12 md:col-6">
                                            <label for="country" class="font-semibold">{{ $t('Country') }}</label>
                                            <Select 
                                                id="country"
                                                v-model="form.country_id" 
                                                :options="countries" 
                                                optionLabel="name" 
                                                optionValue="id"
                                                :placeholder="$t('Select Country')"
                                                class="w-full"
                                                :class="{ 'p-invalid': form.errors.country_id }"
                                                :disabled="form.processing" />
                                            <small v-if="form.errors.country_id" class="p-error">{{ form.errors.country_id }}</small>
                                        </div>

                                        <!-- Link Type -->
                                        <div class="field col-12">
                                            <label for="link_type" class="font-semibold">{{ $t('Link Type') }}</label>
                                            <Select 
                                                id="link_type"
                                                v-model="form.link_type" 
                                                :options="linkTypeOptions" 
                                                optionLabel="label" 
                                                optionValue="value"
                                                :placeholder="$t('Select Link Type')"
                                                class="w-full"
                                                :class="{ 'p-invalid': form.errors.link_type }"
                                                :disabled="form.processing" />
                                            <small v-if="form.errors.link_type" class="p-error">{{ form.errors.link_type }}</small>
                                        </div>

                                        <!-- Categories -->
                                        <div class="field col-12">
                                            <label class="font-semibold">{{ $t('Categories') }}</label>
                                            <div class="flex flex-wrap gap-3 mt-2">
                                                <div class="flex align-items-center">
                                                    <Checkbox id="gambling" v-model="form.gambling" :binary="true" />
                                                    <label for="gambling" class="ml-2">{{ $t('Gambling') }}</label>
                                                </div>
                                                <div class="flex align-items-center">
                                                    <Checkbox id="cdb" v-model="form.cdb" :binary="true" />
                                                    <label for="cdb" class="ml-2">{{ $t('CDB') }}</label>
                                                </div>
                                                <div class="flex align-items-center">
                                                    <Checkbox id="cripto" v-model="form.cripto" :binary="true" />
                                                    <label for="cripto" class="ml-2">{{ $t('Crypto') }}</label>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Features -->
                                        <div class="field col-12">
                                            <label class="font-semibold">{{ $t('Features') }}</label>
                                            <div class="flex flex-wrap gap-3 mt-2">
                                                <div class="flex align-items-center">
                                                    <Checkbox id="sponsor" v-model="form.sponsor" :binary="true" />
                                                    <label for="sponsor" class="ml-2">{{ $t('Sponsor') }}</label>
                                                </div>
                                                <div class="flex align-items-center">
                                                    <Checkbox id="menu" v-model="form.menu" :binary="true" />
                                                    <label for="menu" class="ml-2">{{ $t('Menu') }}</label>
                                                </div>
                                                <div class="flex align-items-center">
                                                    <Checkbox id="banner" v-model="form.banner" :binary="true" />
                                                    <label for="banner" class="ml-2">{{ $t('Banner') }}</label>
                                                </div>
                                                <div class="flex align-items-center">
                                                    <Checkbox id="promo" v-model="form.promo" :binary="true" />
                                                    <label for="promo" class="ml-2">{{ $t('Promo') }}</label>
                                                </div>
                                                <div class="flex align-items-center">
                                                    <Checkbox id="google_news" v-model="form.google_news" :binary="true" />
                                                    <label for="google_news" class="ml-2">{{ $t('Google News') }}</label>
                                                </div>
                                                <div class="flex align-items-center">
                                                    <Checkbox id="embed" v-model="form.embed" :binary="true" />
                                                    <label for="embed" class="ml-2">{{ $t('Embed') }}</label>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Restrictions -->
                                        <div class="field col-12">
                                            <div class="flex align-items-center mb-2">
                                                <Checkbox id="restrict" v-model="form.restrict" :binary="true" />
                                                <label for="restrict" class="ml-2 font-semibold">{{ $t('Restriction') }}</label>
                                            </div>
                                            <InputText 
                                                v-if="form.restrict"
                                                id="restrict_detail" 
                                                v-model="form.restrict_detail" 
                                                class="w-full"
                                                :placeholder="$t('Inform about restrictions')" />
                                        </div>

                                        <!-- Archive Article -->
                                        <div class="field col-12">
                                            <div class="flex align-items-center mb-2">
                                                <Checkbox id="archive_article" v-model="form.archive_article" :binary="true" />
                                                <label for="archive_article" class="ml-2 font-semibold">{{ $t('Archive article') }}</label>
                                            </div>
                                            <InputText 
                                                v-if="form.archive_article"
                                                id="archive_due" 
                                                v-model="form.archive_due" 
                                                class="w-full"
                                                :placeholder="$t('How long')" />
                                        </div>

                                        <!-- Links and Images -->
                                        <div class="field col-12 md:col-6">
                                            <label class="font-semibold">{{ $t('Number of links in the article') }}</label>
                                            <div class="flex gap-3 mt-2">
                                                <div v-for="option in linksOptions" :key="option.value" class="flex align-items-center">
                                                    <RadioButton :id="`links_${option.value}`" v-model="form.links" :value="option.value" />
                                                    <label :for="`links_${option.value}`" class="ml-2">{{ option.label }}</label>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="field col-12 md:col-6">
                                            <label class="font-semibold">{{ $t('Maximum number of images in content') }}</label>
                                            <div class="flex gap-3 mt-2">
                                                <div v-for="option in imagesOptions" :key="option.value" class="flex align-items-center">
                                                    <RadioButton :id="`images_${option.value}`" v-model="form.images" :value="option.value" />
                                                    <label :for="`images_${option.value}`" class="ml-2">{{ option.label }}</label>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Last Posted -->
                                        <div class="field col-12">
                                            <label for="last_posted" class="font-semibold">{{ $t('Last Posted') }}</label>
                                            <InputText 
                                                id="last_posted" 
                                                v-model="form.last_posted" 
                                                type="date"
                                                class="w-full"
                                                :class="{ 'p-invalid': form.errors.last_posted }"
                                                :disabled="form.processing" />
                                            <small v-if="form.errors.last_posted" class="p-error">{{ form.errors.last_posted }}</small>
                                        </div>

                                        <!-- Seller and Team -->
                                        <div class="field col-12 md:col-6">
                                            <label for="seller" class="font-semibold">{{ $t('Seller') }}</label>
                                            <Select 
                                                id="seller"
                                                v-model="form.seller_id" 
                                                :options="sellers" 
                                                optionLabel="name" 
                                                optionValue="id"
                                                :placeholder="$t('Select Seller')"
                                                class="w-full"
                                                :class="{ 'p-invalid': form.errors.seller_id }"
                                                :disabled="form.processing" />
                                            <small v-if="form.errors.seller_id" class="p-error">{{ form.errors.seller_id }}</small>
                                        </div>

                                        <div class="field col-12 md:col-6">
                                            <label for="team" class="font-semibold">{{ $t('Team') }}</label>
                                            <Select 
                                                id="team"
                                                v-model="form.team_id" 
                                                :options="teams" 
                                                optionLabel="name" 
                                                optionValue="id"
                                                :placeholder="$t('Select Team')"
                                                class="w-full"
                                                :class="{ 'p-invalid': form.errors.team_id }"
                                                :disabled="form.processing" />
                                            <small v-if="form.errors.team_id" class="p-error">{{ form.errors.team_id }}</small>
                                        </div>

                                        <!-- Observations -->
                                        <div class="field col-12">
                                            <label for="obs" class="font-semibold">{{ $t('Observations') }}</label>
                                            <Textarea 
                                                id="obs" 
                                                v-model="form.obs" 
                                                rows="3" 
                                                class="w-full"
                                                :class="{ 'p-invalid': form.errors.obs }" />
                                            <small v-if="form.errors.obs" class="p-error">{{ form.errors.obs }}</small>
                                        </div>

                                        <!-- Rules -->
                                        <div class="field col-12">
                                            <label for="rules" class="font-semibold">{{ $t('Rules for content') }}</label>
                                            <Textarea 
                                                id="rules" 
                                                v-model="form.rules" 
                                                rows="3" 
                                                class="w-full"
                                                :class="{ 'p-invalid': form.errors.rules }" />
                                            <small v-if="form.errors.rules" class="p-error">{{ form.errors.rules }}</small>
                                        </div>

                                        <!-- Example Article -->
                                        <div class="field col-12">
                                            <label for="example_article" class="font-semibold">{{ $t('Example article') }}</label>
                                            <InputText 
                                                id="example_article" 
                                                v-model="form.example_article" 
                                                class="w-full"
                                                :class="{ 'p-invalid': form.errors.example_article }"
                                                :disabled="form.processing" />
                                            <small v-if="form.errors.example_article" class="p-error">{{ form.errors.example_article }}</small>
                                        </div>

                                        <!-- Owner Info -->
                                        <div class="field col-12 md:col-8">
                                            <label for="owner_name" class="font-semibold">{{ $t('Owner name') }}</label>
                                            <InputText 
                                                id="owner_name" 
                                                v-model="form.owner_name" 
                                                class="w-full"
                                                :disabled="form.processing" />
                                        </div>

                                        <div class="field col-12 md:col-4">
                                            <label for="owner_role" class="font-semibold">{{ $t('Owner role') }}</label>
                                            <InputText 
                                                id="owner_role" 
                                                v-model="form.owner_role" 
                                                class="w-full"
                                                :disabled="form.processing" />
                                        </div>

                                        <div class="field col-12 md:col-6">
                                            <label for="owner_email" class="font-semibold">{{ $t('Owner email') }}</label>
                                            <InputText 
                                                id="owner_email" 
                                                v-model="form.owner_email" 
                                                class="w-full"
                                                :disabled="form.processing" />
                                        </div>

                                        <div class="field col-12 md:col-6">
                                            <label for="owner_phone" class="font-semibold">{{ $t('Owner phone') }}</label>
                                            <InputText 
                                                id="owner_phone" 
                                                v-model="form.owner_phone" 
                                                class="w-full"
                                                :disabled="form.processing" />
                                        </div>

                                        <!-- Payment Info -->
                                        <div class="field col-12">
                                            <label for="bank" class="font-semibold">{{ $t('Site bank') }}</label>
                                            <Textarea 
                                                id="bank" 
                                                v-model="form.bank" 
                                                rows="3" 
                                                class="w-full" />
                                        </div>

                                        <div class="field col-12">
                                            <label for="global_account" class="font-semibold">{{ $t('Global account') }}</label>
                                            <InputText 
                                                id="global_account" 
                                                v-model="form.global_account" 
                                                class="w-full" />
                                        </div>

                                        <div class="field col-12 md:col-4">
                                            <label for="pix" class="font-semibold">{{ $t('Site pix') }}</label>
                                            <InputText 
                                                id="pix" 
                                                v-model="form.pix" 
                                                class="w-full" />
                                        </div>

                                        <div class="field col-12 md:col-4">
                                            <label for="paypal" class="font-semibold">{{ $t('Site paypal') }}</label>
                                            <InputText 
                                                id="paypal" 
                                                v-model="form.paypal" 
                                                class="w-full" />
                                        </div>

                                        <div class="field col-12 md:col-4">
                                            <label for="phone" class="font-semibold">{{ $t('Site phone') }}</label>
                                            <InputText 
                                                id="phone" 
                                                v-model="form.phone" 
                                                class="w-full" />
                                        </div>

                                        <div class="field col-12 md:col-6">
                                            <label for="instagram" class="font-semibold">{{ $t('Site instagram') }}</label>
                                            <InputText 
                                                id="instagram" 
                                                v-model="form.instagram" 
                                                class="w-full" />
                                        </div>

                                        <div class="field col-12 md:col-6">
                                            <label for="facebook" class="font-semibold">{{ $t('Site facebook') }}</label>
                                            <InputText 
                                                id="facebook" 
                                                v-model="form.facebook" 
                                                class="w-full" />
                                        </div>
                                    </div>

                                    <div class="flex gap-2 mt-4">
                                        <Button 
                                            type="submit"
                                            :label="$t('Save')" 
                                            icon="pi pi-check"
                                            :loading="form.processing" />
                                        
                                        <Button 
                                            :label="$t('Cancel')" 
                                            icon="pi pi-times" 
                                            severity="secondary"
                                            @click="$inertia.visit(route('sites.index'))" />
                                    </div>

                                    <Message v-if="form.recentlySuccessful" severity="success" :closable="false" class="mt-3">
                                        {{ $t('Site saved successfully.') }}
                                    </Message>
                                </form>
                            </template>
                        </Card>
                    </div>
                </div>
            </TabPanel>

            <!-- Offers Tab -->
            <TabPanel>
                <template #header>
                    <i class="pi pi-dollar mr-2"></i>
                    {{ $t('Offers') }}
                </template>
                
                <div class="grid gap-6">
                    <!-- Pricing Tables -->
                    <div v-for="(offer, offerIndex) in form.offers" :key="`offer-${offerIndex}`" class="col-12">
                        <Card>
                            <template #title>
                                <div class="flex align-items-center justify-content-between">
                                    <span>{{ $t('Pricing table for') }} {{ offer.seller.name }}</span>
                                    <Button 
                                        :label="$t('Remove pricing table')" 
                                        icon="pi pi-trash" 
                                        severity="danger" 
                                        size="small"
                                        @click="removePricingTable(offer.id)" />
                                </div>
                            </template>
                            
                            <template #content>
                                <DataTable :value="[{...offer.default, _sellerId: offer.seller.id}, ...offer.types.map(t => ({...t, _sellerId: offer.seller.id}))]" class="p-datatable-sm">
                                    <!-- Type Column -->
                                    <Column :header="$t('Type')" style="width: 20%">
                                        <template #body="slotProps">
                                            <span class="font-semibold">
                                                {{ slotProps.index === 0 ? $t('Default') : slotProps.data.name }}
                                            </span>
                                        </template>
                                    </Column>

                                    <!-- Available Column -->
                                    <Column :header="$t('Available')" style="width: 15%">
                                        <template #body="slotProps">
                                            <Checkbox 
                                                v-if="slotProps.index > 0"
                                                :id="`offer-type-${offerIndex}-${slotProps.index}`"
                                                v-model="slotProps.data.available" 
                                                :binary="true"
                                                :disabled="!canEditPricing(slotProps.data._sellerId)" />
                                        </template>
                                    </Column>

                                    <!-- Cost Column -->
                                    <Column :header="$t('Cost')" style="width: 30%">
                                        <template #body="slotProps">
                                            <div :class="{'opacity-50': slotProps.index > 0 && !slotProps.data.available}">
                                                <InputGroup>
                                                    <InputText 
                                                        v-model="slotProps.data.cost"
                                                        :disabled="(slotProps.index > 0 && !slotProps.data.available) || !canEditPricing(slotProps.data._sellerId)"
                                                        @change="computeSuggestedForType(slotProps.data)"
                                                        class="text-right" />
                                                    <Select
                                                        v-model="slotProps.data.cost_coin"
                                                        :options="[
                                                            { label: 'BRL', value: 'BRL' },
                                                            { label: 'EUR', value: 'EUR' },
                                                            { label: 'USD', value: 'USD' },
                                                            { label: 'GBP', value: 'GBP' }
                                                        ]"
                                                        optionLabel="label"
                                                        optionValue="value"
                                                        :disabled="(slotProps.index > 0 && !slotProps.data.available) || !canEditPricing(slotProps.data._sellerId)"
                                                        @change="updateCoinForType(slotProps.data)"
                                                        style="width: 80px" />
                                                </InputGroup>
                                            </div>
                                        </template>
                                    </Column>

                                    <!-- Sale Column -->
                                    <Column :header="$t('Sale')" style="width: 35%">
                                        <template #body="slotProps">
                                            <div :class="{'opacity-50': slotProps.index > 0 && !slotProps.data.available}">
                                                <InputGroup>
                                                    <InputText 
                                                        v-model="slotProps.data.sale"
                                                        :disabled="(slotProps.index > 0 && !slotProps.data.available) || !canEditPricing(slotProps.data._sellerId)"
                                                        class="text-right" />
                                                    <InputGroupAddon>{{ slotProps.data.sale_coin }}</InputGroupAddon>
                                                </InputGroup>
                                                <small 
                                                    v-if="canEditPricing(slotProps.data._sellerId) && (slotProps.index === 0 || slotProps.data.available)" 
                                                    class="text-primary mt-1 block">
                                                    {{ $t('Suggested') }}: {{ slotProps.data.suggested || '' }}
                                                </small>
                                            </div>
                                        </template>
                                    </Column>
                                </DataTable>
                            </template>
                        </Card>
                    </div>

                    <!-- Add Pricing Table Button -->
                    <div class="col-12" v-if="!form.offers.some(offer => canEditPricing(offer.seller.id))">
                        <Card>
                            <template #content>
                                <div class="text-center">
                                    <Button 
                                        :label="$t('Add pricing table for Linkarme')" 
                                        icon="pi pi-plus" 
                                        @click="addPricingTableForLinkarme" />
                                </div>
                            </template>
                        </Card>
                    </div>
                </div>
            </TabPanel>

            <!-- Orders Tab -->
            <TabPanel>
                <template #header>
                    <i class="pi pi-shopping-cart mr-2"></i>
                    {{ $t('Orders') }}
                </template>
                
                <Card>
                    <template #content>
                        <DataTable :value="site.orders" paginator :rows="10" dataKey="id" class="p-datatable-sm">
                            <Column field="url" :header="$t('URL')" sortable></Column>
                            <Column field="created_at" :header="$t('Date')" sortable>
                                <template #body="slotProps">
                                    {{ new Date(slotProps.data.created_at).toLocaleString() }}
                                </template>
                            </Column>
                        </DataTable>
                    </template>
                </Card>
            </TabPanel>

            <!-- Views Tab -->
            <TabPanel>
                <template #header>
                    <i class="pi pi-eye mr-2"></i>
                    {{ $t('Views') }}
                </template>
                
                <Card>
                    <template #content>
                        <DataTable :value="site.views" paginator :rows="10" dataKey="id" class="p-datatable-sm">
                            <Column field="client.name" :header="$t('Client')" sortable></Column>
                            <Column field="created_at" :header="$t('Date')" sortable>
                                <template #body="slotProps">
                                    {{ new Date(slotProps.data.created_at).toLocaleString() }}
                                </template>
                            </Column>
                        </DataTable>
                    </template>
                </Card>
            </TabPanel>
        </TabView>

        <!-- Confirmation Dialog -->
        <Dialog v-model:visible="confirmDialog" modal header="Confirm Delete" :style="{width: '450px'}">
            <div class="flex align-items-center gap-3 mb-3">
                <i class="pi pi-exclamation-triangle text-orange-500" style="font-size: 2rem"></i>
                <span>{{ $t('Are you sure you want to permanently delete this site?') }}</span>
            </div>
            <template #footer>
                <Button :label="$t('Cancel')" icon="pi pi-times" severity="secondary" @click="confirmDialog = false" />
                <Button :label="$t('Delete')" icon="pi pi-check" severity="danger" @click="destroy; confirmDialog = false" />
            </template>
        </Dialog>
    </AdminMainLayout>
</template>

<style scoped>
.field {
    margin-bottom: 1.5rem;
}

.field label {
    display: block;
    margin-bottom: 0.5rem;
}

.p-error {
    color: var(--red-500);
    font-size: 0.875rem;
    margin-top: 0.25rem;
    display: block;
}
</style>